import { Link } from "react-router-dom";
import { Activity, Plus, Pencil, Cloud } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import { Section, Loadable, Empty, Status, Info, ActionButton, PageView, PageButtons, useLoad, fmt, can, TZ_OPTIONS } from "@/components/control/kit";

const STATES = [["entwurf", "Entwurf"], ["untersuchung", "Störung: in Untersuchung"], ["behoben", "Störung: behoben"],
  ["geplant", "Wartung: geplant"], ["abgeschlossen", "Wartung: abgeschlossen"]].map(([value, label]) => ({ value, label }));
const AFFECTED = ["Anmeldung", "Mitglieder-App", "Aktivitäten", "Benachrichtigungen", "Control OS"].map((x) => ({ value: x, label: x }));

const incidentFields = (i = {}) => [
  { name: "kind", label: "Art", type: "select", required: true, default: i.kind, options: [{ value: "incident", label: "Störung" }, { value: "maintenance", label: "Wartung" }] },
  { name: "title", label: "Titel", required: true, default: i.title },
  { name: "status", label: "Status", type: "select", required: true, default: i.status, options: STATES,
    help: "Störung: Entwurf, in Untersuchung, behoben. Wartung: Entwurf, geplant, abgeschlossen." },
  { name: "starts_at", label: "Beginn", type: "datetime-local", default: i.starts_at },
  { name: "ends_at", label: "Ende (voraussichtlich)", type: "datetime-local", default: i.ends_at },
  { name: "affected", label: "Betroffene Bereiche", type: "checks", options: AFFECTED, default: i.affected || [] },
  { name: "impact", label: "Auswirkung", type: "textarea", default: i.impact },
  { name: "public_note", label: "Hinweis für Mitglieder (erscheint in der App)", type: "textarea", default: i.public_note },
  { name: "internal_note", label: "Interne Notiz", type: "textarea", default: i.internal_note },
];

const STATES_FOR = { incident: STATES.slice(0, 3), maintenance: [STATES[0], STATES[3], STATES[4]] };
const tzField = (i = {}) => ({ name: "timezone", label: "Zeitzone", type: "select", required: true, default: i.timezone || "Europe/Zurich", options: TZ_OPTIONS });

// One field set per kind: incidents and maintenance never share status values.
const kindFields = (i = {}, kind = "incident") => incidentFields(i).filter((f) => f.name !== "kind")
  .map((f) => (f.name === "status" ? { ...f, options: STATES_FOR[kind], help: undefined } : f.name === "title" ? { ...f, label: "Betroffener Dienst" } : f))
  .flatMap((f) => (f.name === "ends_at" ? [f, tzField(i)] : [f]));

const newIncidentFields = kindFields();

const maintenanceFields = [
  { name: "title", label: "Betroffener Dienst", required: true },
  { name: "status", label: "Status", type: "select", required: true, options: [STATES[0], STATES[3]],
    help: "Entwurf bleibt intern; „Wartung: geplant“ macht den Hinweis für Mitglieder sichtbar." },
  { name: "starts_at", label: "Beginn", type: "datetime-local", required: true },
  { name: "ends_at", label: "Ende (geplant)", type: "datetime-local", required: true },
  tzField(),
  { name: "affected", label: "Betroffene Bereiche", type: "checks", options: AFFECTED, default: [] },
  { name: "public_note", label: "Hinweis für Mitglieder (erscheint in der App)", type: "textarea", required: true },
  { name: "internal_note", label: "Interne Notiz", type: "textarea" },
];

function CreateButtons({ reload }) {
  return (
    <>
      <ActionButton label="Störung erfassen" icon={Plus} variant="default" testId="create-incident" fields={newIncidentFields} onDone={reload} submitLabel="Statushinweis speichern"
        page="stoerung" title="Störung erfassen" pageSubtitle="Trenne den sichtbaren Statushinweis von internen Untersuchungsnotizen."
        onSubmit={async (v) => { await api.post("/system/incidents", { ...v, kind: "incident" }); return "Störung gespeichert."; }} />
      <ActionButton label="Wartung ankündigen" icon={Plus} testId="create-maintenance" fields={maintenanceFields} onDone={reload} submitLabel="Wartung speichern"
        page="wartung" title="Wartung ankündigen" pageSubtitle="Geplante Zeiten sind keine bestätigte Wiederverfügbarkeit."
        onSubmit={async (v) => { await api.post("/system/incidents", { ...v, kind: "maintenance" }); return "Wartung gespeichert."; }} />
    </>
  );
}

function Incidents() {
  const st = useLoad("/system/incidents");
  return (
    <Section title="Störungen und Wartungen" testId="incidents" actions={st.data?.can_manage && <CreateButtons reload={st.reload} />}>
      <Loadable state={st}>
        {(d) => (d.incidents.length === 0 ? <Empty text="Keine Störungen oder Wartungen erfasst." /> : (
          <ul className="divide-y divide-border">
            {d.incidents.map((i) => (
              <li key={i.id} className="flex flex-wrap items-start justify-between gap-2 py-3" data-testid={`incident-${i.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-brand-purple">{i.kind === "maintenance" ? "Wartung" : "Störung"}: {i.title}</p>
                  <p className="text-xs text-muted-foreground">{fmt(i.starts_at)} – {fmt(i.ends_at)} ({i.timezone || "Europe/Zurich"}) · {(i.affected || []).join(", ") || "keine Bereiche angegeben"}</p>
                  {i.public_note && <p className="mt-1 text-sm">Hinweis: {i.public_note}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Status value={i.status} />
                  {d.can_manage && <ActionButton label="Aktualisieren" icon={Pencil} testId={`update-incident-${i.id}`} fields={kindFields(i, i.kind)} onDone={st.reload}
                    page={`stoerung-${i.id}`} title={i.kind === "maintenance" ? "Wartung aktualisieren" : "Störung aktualisieren"}
                    pageSubtitle="Aktualisiere den sichtbaren Statushinweis dieses Eintrags." submitLabel="Speichern"
                    onSubmit={async (v) => { await api.patch(`/system/incidents/${i.id}`, { ...v, kind: i.kind }); return "Eintrag aktualisiert."; }} />}
                </div>
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </Section>
  );
}

// C117: while checks run, no figures are shown; nothing is presented as known before the server answered.
function StatusPending() {
  return (
    <div className="max-w-3xl space-y-2.5" role="status" data-testid="service-status-unknown">
      <h2 className="text-2xl font-bold text-brand-purple">Dienststatus noch unbekannt</h2>
      <p className="text-sm text-zinc-600">Die aktuellen Dienstprüfungen sind noch nicht abgeschlossen.</p>
      <div className="rounded-xl bg-[#EEEAF7] px-4 py-3"><p className="font-bold text-brand-purple">Aktualisierung läuft</p>
        <p className="text-sm text-zinc-600">Noch keine abschliessenden Inhalte oder Zahlen verfügbar.</p></div>
      {["Anmeldung", "E-Mail", "Medien", "Gesamtaussage"].map((n) => (
        <div key={n} className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-4">
          <p className="font-bold text-brand-purple">{n}</p>
          <div className="mt-3 h-2 w-2/3 rounded-full bg-[#E6E1F0] motion-safe:animate-pulse" /><div className="mt-2 h-2 w-1/3 rounded-full bg-[#EEEAF7] motion-safe:animate-pulse" />
        </div>
      ))}
    </div>
  );
}

const overall = (services) => (services.some((s) => s.state === "gestört")
  ? "Störung festgestellt – siehe betroffene Dienste"
  : "Unbekannt – keine dauerhafte Überwachung eingerichtet. Diese Momentaufnahme zeigt keine Störung.");

const LINK = "rounded-lg border border-brand-purple/30 bg-white px-3 py-2 text-sm font-semibold text-brand-purple hover:bg-secondary";
function AdminLinks() {
  const { user } = useAuth();
  if (user?.role !== "super_admin") return null;
  return (
    <>
      <Link to="/qualitaet" data-testid="qa-link" className={LINK}>Prüfungen und Freigaben</Link>
      <Link to="/betriebsaufwand" data-testid="ops-effort-link" className={LINK}>Betriebsaufwand</Link>
      <Link to="/komponenten" data-testid="components-link" className={LINK}>Gemeinsame Komponenten</Link>
    </>
  );
}

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");

function IntegrationCards() {
  const st = useLoad("/settings/integrations");
  return (
    <Loadable state={st}>
      {(d) => (
        <div className="space-y-3" data-testid="integrations-cards">
          {d.integrations.map((i) => (
            <div key={i.name} className="flex items-start gap-3 rounded-2xl border border-[#E6E1F0] bg-white p-4" data-testid={`integration-card-${slug(i.name)}`}>
              <Cloud className="mt-0.5 h-5 w-5 shrink-0 text-brand-purple" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-bold text-brand-purple">{i.name}</p>
                <div className="mt-1"><Status value={i.state} /></div>
                <p className="mt-2 text-sm text-zinc-600">{i.scope} · {i.note}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Loadable>
  );
}

// C95: own full page; the data and the settings:view right are the same as in the settings tab.
function IntegrationsPage() {
  const { user } = useAuth();
  if (!can(user, "settings:view")) return null;
  return (
    <PageView page="integrationen" label="Integrationen und Verbindungen" icon={Cloud} testId="integrations-view" title="Integrationen und Verbindungen"
      subtitle="Zeigt den tatsächlichen Stand. Eine Vorbereitung ist noch keine bestätigte Verbindung.">
      {({ back }) => <><IntegrationCards /><PageButtons back={back} testId="integrations-view" /></>}
    </PageView>
  );
}

export default function SystemStatus() {
  const st = useLoad("/system/status");
  return (
    <div>
      <PageHeader title="Systemstatus" subtitle="Momentaufnahme der Dienste zum Abfragezeitpunkt – keine geschätzten Werte." icon={Activity} actions={<><IntegrationsPage /><AdminLinks /></>} />
      {st.loading && !st.data ? <StatusPending /> : <Loadable state={st}>
        {(d) => (
          <div className="grid gap-5 lg:grid-cols-2">
            <Section title={`Dienste · geprüft ${fmt(d.checked_at)}`} testId="system-services" actions={
              <button type="button" onClick={st.reload} data-testid="refresh-status" className="text-sm font-medium text-brand-purple underline">Neu prüfen</button>
            }>
              <ul className="divide-y divide-border">
                {d.services.map((s) => (
                  <li key={s.key} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`service-${s.key}`}>
                    <div><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.note}</p></div>
                    <Status value={s.state} />
                  </li>
                ))}
              </ul>
              <div className="mt-4"><Info items={[["Umgebung", d.environment], ["Gesamtaussage", overall(d.services)],
                ["Fehlgeschlagene Zweitfaktor-Versuche (24 h)", d.signals.failed_second_factor_24h]]} /></div>
            </Section>
            <Incidents />
          </div>
        )}
      </Loadable>}
    </div>
  );
}
